// TMS Design System Typography
// Based on TMS-Design-System.md specifications

export const typography = {
  // Font Families
  fontFamily: {
    sans: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(', '),
    mono: [
      '"JetBrains Mono"',
      '"Fira Code"',
      '"SF Mono"',
      'Monaco',
      'Consolas',
      '"Courier New"',
      'monospace',
    ].join(', '),
  },

  // Font Sizes
  fontSize: {
    // Display Sizes
    'display-1': '3rem', // 48px
    'display-2': '2.5rem', // 40px

    // Heading Sizes
    h1: '2.25rem', // 36px
    h2: '1.75rem', // 28px
    h3: '1.5rem', // 24px
    h4: '1.25rem', // 20px
    h5: '1.125rem', // 18px
    h6: '1rem', // 16px

    // Body Sizes
    'body-lg': '1.125rem', // 18px
    body: '1rem', // 16px
    'body-sm': '0.875rem', // 14px
    'body-xs': '0.75rem', // 12px

    // Utility Sizes
    xs: '0.75rem', // 12px
    sm: '0.875rem', // 14px
    base: '1rem', // 16px
    lg: '1.125rem', // 18px
    xl: '1.25rem', // 20px
    '2xl': '1.5rem', // 24px
    '3xl': '1.75rem', // 28px
    '4xl': '2.25rem', // 36px
    '5xl': '2.5rem', // 40px
    '6xl': '3rem', // 48px
  },

  // Font Weights
  fontWeight: {
    light: '300',
    normal: '400',
    medium: '500',
    semibold: '600',
    bold: '700',
    extrabold: '800',
  },

  // Line Heights
  lineHeight: {
    none: '1',
    tight: '1.1',
    heading: '1.2',
    relaxed: '1.4',
    data: '1.4',
    base: '1.5',
    loose: '1.75',
  },

  // Letter Spacing
  letterSpacing: {
    tighter: '-0.05em',
    tight: '-0.025em',
    normal: '0',
    wide: '0.025em',
    wider: '0.05em',
    widest: '0.1em',
  },
} as const;

// CSS Variables for typography
export const typographyVariables = {
  '--font-family-sans': typography.fontFamily.sans,
  '--font-family-mono': typography.fontFamily.mono,

  '--font-size-xs': typography.fontSize.xs,
  '--font-size-sm': typography.fontSize.sm,
  '--font-size-base': typography.fontSize.base,
  '--font-size-lg': typography.fontSize.lg,
  '--font-size-xl': typography.fontSize.xl,

  '--font-weight-normal': typography.fontWeight.normal,
  '--font-weight-medium': typography.fontWeight.medium,
  '--font-weight-semibold': typography.fontWeight.semibold,
  '--font-weight-bold': typography.fontWeight.bold,

  '--line-height-base': typography.lineHeight.base,
  '--line-height-heading': typography.lineHeight.heading,
  '--line-height-data': typography.lineHeight.data,
} as const;

// Text Styles for common use cases
export const textStyles = {
  // Display styles
  'display-1': {
    fontSize: typography.fontSize['display-1'],
    fontWeight: typography.fontWeight.bold,
    lineHeight: typography.lineHeight.heading,
    letterSpacing: typography.letterSpacing.tight,
  },
  'display-2': {
    fontSize: typography.fontSize['display-2'],
    fontWeight: typography.fontWeight.bold,
    lineHeight: typography.lineHeight.heading,
    letterSpacing: typography.letterSpacing.tight,
  },

  // Heading styles
  h1: {
    fontSize: typography.fontSize.h1,
    fontWeight: typography.fontWeight.bold,
    lineHeight: typography.lineHeight.heading,
  },
  h2: {
    fontSize: typography.fontSize.h2,
    fontWeight: typography.fontWeight.semibold,
    lineHeight: typography.lineHeight.heading,
  },
  h3: {
    fontSize: typography.fontSize.h3,
    fontWeight: typography.fontWeight.semibold,
    lineHeight: typography.lineHeight.heading,
  },
  h4: {
    fontSize: typography.fontSize.h4,
    fontWeight: typography.fontWeight.medium,
    lineHeight: typography.lineHeight.heading,
  },
  h5: {
    fontSize: typography.fontSize.h5,
    fontWeight: typography.fontWeight.medium,
    lineHeight: typography.lineHeight.heading,
  },
  h6: {
    fontSize: typography.fontSize.h6,
    fontWeight: typography.fontWeight.medium,
    lineHeight: typography.lineHeight.heading,
  },

  // Body styles
  'body-lg': {
    fontSize: typography.fontSize['body-lg'],
    fontWeight: typography.fontWeight.normal,
    lineHeight: typography.lineHeight.base,
  },
  body: {
    fontSize: typography.fontSize.body,
    fontWeight: typography.fontWeight.normal,
    lineHeight: typography.lineHeight.base,
  },
  'body-sm': {
    fontSize: typography.fontSize['body-sm'],
    fontWeight: typography.fontWeight.normal,
    lineHeight: typography.lineHeight.base,
  },
  'body-xs': {
    fontSize: typography.fontSize['body-xs'],
    fontWeight: typography.fontWeight.normal,
    lineHeight: typography.lineHeight.base,
  },

  // Utility styles
  label: {
    fontSize: typography.fontSize['body-sm'],
    fontWeight: typography.fontWeight.medium,
    lineHeight: typography.lineHeight.data,
  },
  caption: {
    fontSize: typography.fontSize['body-xs'],
    fontWeight: typography.fontWeight.normal,
    lineHeight: typography.lineHeight.data,
  },
  code: {
    fontFamily: typography.fontFamily.mono,
    fontSize: typography.fontSize['body-sm'],
    fontWeight: typography.fontWeight.normal,
    lineHeight: typography.lineHeight.data,
  },
} as const;

// Type definitions
export type Typography = typeof typography;
export type FontSize = keyof Typography['fontSize'];
export type FontWeight = keyof Typography['fontWeight'];
export type LineHeight = keyof Typography['lineHeight'];
export type TextStyle = keyof typeof textStyles;
